import React, { useState, useEffect, useRef } from 'react';
import { Calculator, CheckCircle, XCircle, Download, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import { performGlobalSearch } from '../services/searchAPI';
import { useSettings } from '../contexts/SettingsContext';
import settingsService from '../services/settingsService';
import { getPdfBrand } from '../utils/pdfBranding';

export default function ImmigrationCalculator() {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [langue1Oral, setLangue1Oral] = useState('');
  const [langue1Ecoute, setLangue1Ecoute] = useState('');
  const [langue1Lecture, setLangue1Lecture] = useState('');
  const [langue1Ecriture, setLangue1Ecriture] = useState('');
  const [langue2, setLangue2] = useState('non');
  const [etudes, setEtudes] = useState(0);
  const [experience, setExperience] = useState('');
  const [age, setAge] = useState('');
  const [emploiReserve, setEmploiReserve] = useState('non');
  const [adaptation, setAdaptation] = useState([]);

  // Recherche client/lead
  const [personQuery, setPersonQuery] = useState('');
  const [personResults, setPersonResults] = useState([]); // { id, label, type, raw }
  const [isSearching, setIsSearching] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null); // { id, label, type, raw }
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    if (!personQuery || personQuery.trim().length < 2) {
      setPersonResults([]);
      return;
    }
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        setIsSearching(true);
        const res = await performGlobalSearch(personQuery.trim());
        const data = res?.data || {};
        const leads = (data.leads || []).map(l => ({
          id: l.id,
          type: 'lead',
          label: l.title || l.subtitle || 'Lead',
          raw: l
        }));
        const clients = (data.clients || []).map(c => ({
          id: c.id,
          type: 'client',
          label: c.title || c.subtitle || 'Client',
          raw: c
        }));
        setPersonResults([ ...clients.slice(0,5), ...leads.slice(0,5) ]);
      } catch (e) {
        setPersonResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
    return () => searchTimeoutRef.current && clearTimeout(searchTimeoutRef.current);
  }, [personQuery]);

  const getLanguePoints = (niveau) => {
    const pointsMap = {
      '9+': 6,
      '8': 5,
      '7': 4,
      'moins7': 0
    };
    return pointsMap[niveau] || 0;
  };

  const getExperiencePoints = (exp) => {
    const pointsMap = {
      '1': 9,
      '2-3': 11,
      '4-5': 13,
      '6+': 15
    };
    return pointsMap[exp] || 0;
  };

  const getAgePoints = (ageValue) => {
    const pointsMap = {
      'moins18': 0,
      '18-35': 12,
      '36': 11,
      '37': 10,
      '38': 9,
      '39': 8,
      '40': 7,
      '41': 6,
      '42': 5,
      '43': 4,
      '44': 3,
      '45': 2,
      '46': 1,
      '47+': 0
    };
    return pointsMap[ageValue] || 0;
  };

  const calculateTotal = () => {
    let total = 0;
    
    // Langue 1
    total += getLanguePoints(langue1Oral);
    total += getLanguePoints(langue1Ecoute);
    total += getLanguePoints(langue1Lecture);
    total += getLanguePoints(langue1Ecriture);
    
    // Langue 2
    if (langue2 === 'oui') total += 4;
    
    // Études
    total += parseInt(etudes) || 0;
    
    // Expérience
    total += getExperiencePoints(experience);
    
    // Âge
    total += getAgePoints(age);
    
    // Emploi réservé
    if (emploiReserve === 'oui') total += 10;
    
    // Faculté d'adaptation
    adaptation.forEach(item => {
      total += parseInt(item) || 0;
    });
    
    return total;
  };

  const handleAdaptationChange = (value, checked) => {
    if (checked) {
      setAdaptation([...adaptation, value]);
    } else {
      setAdaptation(adaptation.filter(item => item !== value));
    }
  };

  const totalPoints = calculateTotal();
  const isEligible = totalPoints >= 67;

  const generatePDF = async () => {
    const doc = new jsPDF();
    // Toujours utiliser le logo de l'entreprise connectée, avec un paramètre
    // anti-cache afin que les PDF reflètent immédiatement un changement de logo.
    const logoUrl = settingsService.getLogoUrl(true, settings.logo);
    const { companyName, primaryColor } = await getPdfBrand({
      companyName: settings.nom,
      logoUrl
    });
    
    // Configuration des couleurs et styles
    const successColor = [34, 197, 94]; // Vert
    const textColor = [31, 41, 55]; // Gris foncé
    
    // Fonction pour convertir l'image en base64 avec compression
    const getImageBase64 = (url) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = function() {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Taille moyenne pour le filigrane (max 200x200 pixels pour meilleure qualité)
          const maxSize = 200;
          let width = this.width;
          let height = this.height;
          
          if (width > maxSize || height > maxSize) {
            const ratio = Math.min(maxSize / width, maxSize / height);
            width = width * ratio;
            height = height * ratio;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          // Dessiner l'image normalement (pas de globalAlpha ici)
          ctx.drawImage(this, 0, 0, width, height);
          
          try {
            // PNG avec meilleure qualité (80%)
            const dataURL = canvas.toDataURL('image/png', 0.8);
            resolve(dataURL);
          } catch (e) {
            reject(e);
          }
        };
        img.onerror = reject;
        img.src = url;
      });
    };
    
    // Ajouter le logo en filigrane avec opacité jsPDF
    try {
      const logoBase64 = await getImageBase64(logoUrl);
      
      // Calculer la position centrale pour le filigrane
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const logoSize = 90; // Taille augmentée du logo en filigrane
      
      // Position centrée
      const x = (pageWidth - logoSize) / 2;
      const y = (pageHeight - logoSize) / 2;
      
      // Définir l'opacité pour le filigrane
      doc.setGState(new doc.GState({opacity: 0.1})); // 10% d'opacité
      
      // Ajouter l'image en filigrane
      doc.addImage(logoBase64, 'PNG', x, y, logoSize, logoSize);
      
      // Réinitialiser l'opacité pour le reste du document
      doc.setGState(new doc.GState({opacity: 1.0}));
      
    } catch (error) {
      console.log('Impossible de charger le logo, génération du PDF sans filigrane:', error);
    }
    
    // En-tête amélioré
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, 210, 35, 'F');
    
    // Titre principal - ligne 1
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text('Calculateur d\'Admissibilité - Entrée Express', 20, 16);
    
    // Sous-titre - ligne 2 (dans le bandeau)
    doc.setFontSize(11);
    doc.setFont(undefined, 'normal');
    doc.text('Programme des travailleurs qualifiés (fédéral)', 20, 26);

    // Nom du client/lead sélectionné (en dessous du bandeau rouge, sur fond blanc)
    if (selectedPerson?.label) {
      doc.setTextColor(...textColor);
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text(`${selectedPerson.type === 'client' ? 'Client' : 'Lead'}: ${selectedPerson.label}`, 20, 45);
    }
    
    // Nom de l'entreprise - coin supérieur droit
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text(companyName, 190, 16, { align: 'right' });
    
    // Date de génération - coin inférieur droit
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    doc.text(`Généré le: ${new Date().toLocaleDateString('fr-FR')}`, 190, 26, { align: 'right' });
    
    let yPosition = selectedPerson?.label ? 70 : 55;
    
    // Résultat principal
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    if (isEligible) {
      doc.setTextColor(...successColor);
    } else {
      doc.setTextColor(...primaryColor);
    }
    doc.text(`Score total: ${totalPoints} / 100 points`, 20, yPosition);
    
    yPosition += 8;
    doc.setFontSize(10);
    doc.text(isEligible 
      ? '✓ ADMISSIBLE (67+ points requis)' 
      : `✗ NON ADMISSIBLE (${67 - totalPoints} point(s) manquant(s))`, 20, yPosition);
    
    yPosition += 15;
    doc.setTextColor(...textColor);
    doc.setFont(undefined, 'bold');
    doc.setFontSize(12);
    doc.text('Détail des points par section:', 20, yPosition);
    
    yPosition += 10;
    doc.setFont(undefined, 'normal');
    doc.setFontSize(9);
    
    // Version compacte - 2 colonnes
    const sections = [
      {
        titre: '1. Langue - 1ère langue',
        points: getLanguePoints(langue1Oral) + getLanguePoints(langue1Ecoute) + 
                getLanguePoints(langue1Lecture) + getLanguePoints(langue1Ecriture),
        max: 24
      },
      {
        titre: '2. Seconde langue',
        points: langue2 === 'oui' ? 4 : 0,
        max: 4
      },
      {
        titre: '3. Études',
        points: parseInt(etudes) || 0,
        max: 25
      },
      {
        titre: '4. Expérience travail',
        points: getExperiencePoints(experience),
        max: 15
      },
      {
        titre: '5. Âge',
        points: getAgePoints(age),
        max: 12
      },
      {
        titre: '6. Emploi réservé',
        points: emploiReserve === 'oui' ? 10 : 0,
        max: 10
      },
      {
        titre: '7. Adaptation',
        points: adaptation.reduce((sum, item) => sum + (parseInt(item) || 0), 0),
        max: 10
      }
    ];
    
    // Affichage en 2 colonnes pour économiser l'espace
    sections.forEach((section, index) => {
      const column = index % 2;
      const row = Math.floor(index / 2);
      const x = column === 0 ? 20 : 110;
      const y = yPosition + (row * 12);
      
      // Titre de section
      doc.setFont(undefined, 'bold');
      doc.text(`${section.titre}:`, x, y);
      doc.text(`${section.points}/${section.max}`, x + 70, y);
      
      // Barre de progression visuelle
      const barWidth = 60;
      const barHeight = 3;
      const progress = Math.min(section.points / section.max, 1);
      
      // Fond de la barre
      doc.setFillColor(240, 240, 240);
      doc.rect(x, y + 2, barWidth, barHeight, 'F');
      
      // Barre de progression
      if (progress > 0) {
        const color = progress >= 0.7 ? successColor : (progress >= 0.4 ? [255, 165, 0] : primaryColor);
        doc.setFillColor(...color);
        doc.rect(x, y + 2, barWidth * progress, barHeight, 'F');
      }
    });
    
    // Ajuster la position après les sections (4 lignes max)
    yPosition += Math.ceil(sections.length / 2) * 12 + 15;
    
    // Résumé compact
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...textColor);
    doc.text('Résumé:', 20, yPosition);
    
    yPosition += 8;
    doc.setFont(undefined, 'normal');
    doc.setFontSize(8);
    
    const resume = [
      `• Langue principale: ${(getLanguePoints(langue1Oral) + getLanguePoints(langue1Ecoute) + getLanguePoints(langue1Lecture) + getLanguePoints(langue1Ecriture))} pts`,
      `• Seconde langue: ${langue2 === 'oui' ? '4' : '0'} pts`,
      `• Études: ${parseInt(etudes) || 0} pts`,
      `• Expérience: ${getExperiencePoints(experience)} pts`,
      `• Âge: ${getAgePoints(age)} pts`,
      `• Emploi réservé: ${emploiReserve === 'oui' ? '10' : '0'} pts`,
      `• Adaptation: ${adaptation.reduce((sum, item) => sum + (parseInt(item) || 0), 0)} pts`
    ];
    
    // Affichage du résumé en 2 colonnes
    resume.forEach((item, index) => {
      const column = index % 2;
      const row = Math.floor(index / 2);
      const x = column === 0 ? 20 : 110;
      const y = yPosition + (row * 8);
      doc.text(item, x, y);
    });
    
    yPosition += Math.ceil(resume.length / 2) * 8 + 15;
    
    // Fonction pour ajouter le filigrane sur une nouvelle page
    const ajouterFiligraneNouvellePage = async () => {
      try {
        const logoBase64 = await getImageBase64(logoUrl);
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const logoSize = 90;
        const x = (pageWidth - logoSize) / 2;
        const y = (pageHeight - logoSize) / 2;
        
        doc.setGState(new doc.GState({opacity: 0.1}));
        doc.addImage(logoBase64, 'PNG', x, y, logoSize, logoSize);
        doc.setGState(new doc.GState({opacity: 1.0}));
      } catch (error) {
        console.log('Filigrane non ajouté');
      }
    };
    
    // Recommandations personnalisées si NON ADMISSIBLE
    if (!isEligible) {
      // Vérifier s'il faut ajouter une nouvelle page
      if (yPosition > 230) {
        doc.addPage();
        await ajouterFiligraneNouvellePage();
        yPosition = 20;
      }
      
      // Titre de la section recommandations
      doc.setFillColor(255, 243, 243); // Fond rose clair
      doc.rect(15, yPosition - 5, 180, 10, 'F');
      doc.setTextColor(...primaryColor);
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text('RECOMMANDATIONS POUR AMELIORER VOTRE SCORE', 20, yPosition + 2);
      
      yPosition += 15;
      doc.setTextColor(...textColor);
      doc.setFontSize(9);
      doc.setFont(undefined, 'normal');
      doc.text(`Il vous manque ${67 - totalPoints} point(s) pour être admissible. Voici des recommandations personnalisées:`, 20, yPosition);
      yPosition += 10;
      
      // Analyser les faiblesses et générer des recommandations
      const recommandations = [];
      
      // 1. Vérifier les compétences linguistiques (max 28 points)
      const languePoints = getLanguePoints(langue1Oral) + getLanguePoints(langue1Ecoute) + 
                          getLanguePoints(langue1Lecture) + getLanguePoints(langue1Ecriture);
      if (languePoints < 20) {
        const potentiel = 24 - languePoints;
        recommandations.push({
          priorite: 'HAUTE',
          domaine: 'Competences linguistiques (1ere langue)',
          score: `${languePoints}/24 pts`,
          gain: `+${potentiel} pts possibles`,
          actions: [
            'Suivre une formation intensive en francais ou anglais',
            'Passer un test officiel (TEF, TCF, IELTS, CELPIP) apres preparation',
            'Viser un score NCLC 9+ dans les 4 competences pour maximiser les points',
            'Pratiquer quotidiennement: lecture, ecoute de podcasts, conversations'
          ]
        });
      }
      
      // 2. Vérifier la seconde langue
      if (langue2 === 'non') {
        recommandations.push({
          priorite: 'MOYENNE',
          domaine: 'Seconde langue officielle',
          score: '0/4 pts',
          gain: '+4 pts possibles',
          actions: [
            'Apprendre la seconde langue officielle (francais si anglais, vice-versa)',
            'Obtenir minimum NCLC 5 dans les 4 competences',
            'Suivre des cours en ligne gratuits (Duolingo, TV5Monde, BBC Learning)'
          ]
        });
      }
      
      // 3. Vérifier les études
      const etudesPoints = parseInt(etudes) || 0;
      if (etudesPoints < 25) {
        const diplomes = {
          '0': 'Aucun diplôme',
          '5': 'Diplôme secondaire',
          '12': 'Diplôme 1 an post-secondaire',
          '15': 'Diplôme 2 ans post-secondaire',
          '21': 'Diplôme 3 ans+ ou Baccalauréat',
          '22': 'Deux diplômes ou plus',
          '23': 'Maîtrise ou diplôme professionnel',
          '25': 'Doctorat (Ph.D.)'
        };
        const potentiel = 25 - etudesPoints;
        recommandations.push({
          priorite: 'MOYENNE',
          domaine: 'Niveau d\'etudes',
          score: `${etudesPoints}/25 pts`,
          gain: `+${potentiel} pts possibles`,
          actions: [
            'Poursuivre vos etudes (diplome superieur)',
            'Faire evaluer vos diplomes etrangers (ECA) si pas encore fait',
            'Considerer un programme d\'etudes au Canada (DEP, AEC, Baccalaureat)',
            'Obtenir une reconnaissance professionnelle ou certification'
          ]
        });
      }
      
      // 4. Vérifier l'expérience de travail
      const expPoints = getExperiencePoints(experience);
      if (expPoints < 15) {
        const potentiel = 15 - expPoints;
        recommandations.push({
          priorite: 'HAUTE',
          domaine: 'Experience de travail',
          score: `${expPoints}/15 pts`,
          gain: `+${potentiel} pts possibles`,
          actions: [
            'Acquerir plus d\'experience dans votre domaine (viser 6+ ans)',
            'Privilegier un emploi dans un metier qualifie (NOC FEER 0, 1, 2, 3)',
            'Obtenir des lettres de reference de vos employeurs',
            'Considerer un permis de travail temporaire au Canada'
          ]
        });
      }
      
      // 5. Vérifier l'âge
      const agePoints = getAgePoints(age);
      if (agePoints < 10) {
        recommandations.push({
          priorite: 'INFO',
          domaine: 'Age',
          score: `${agePoints}/12 pts`,
          gain: age === '47+' ? 'Non modifiable' : 'Facteur temps',
          actions: [
            'Prioriser votre demande rapidement (les points d\'age diminuent)',
            'Compenser par d\'autres facteurs (langue, etudes, experience)',
            age !== '47+' ? 'Vous avez encore du temps pour ameliorer votre profil' : 
                          'Concentrez-vous sur les autres facteurs'
          ]
        });
      }
      
      // 6. Vérifier l'emploi réservé
      if (emploiReserve === 'non') {
        recommandations.push({
          priorite: 'MOYENNE',
          domaine: 'Emploi reserve au Canada',
          score: '0/10 pts',
          gain: '+10 pts possibles',
          actions: [
            'Rechercher un employeur canadien pret a vous embaucher',
            'Obtenir une EIMT (Etude d\'Impact sur le Marche du Travail) positive',
            'Explorer les programmes provinciaux (PEQ, OINP, etc.)',
            'Utiliser des plateformes: Guichet Emplois, LinkedIn, Indeed Canada'
          ]
        });
      }
      
      // 7. Vérifier l'adaptation
      const adaptPoints = adaptation.reduce((sum, item) => sum + (parseInt(item) || 0), 0);
      if (adaptPoints < 10) {
        const potentiel = 10 - adaptPoints;
        recommandations.push({
          priorite: 'BASSE',
          domaine: 'Capacite d\'adaptation',
          score: `${adaptPoints}/10 pts`,
          gain: `+${potentiel} pts possibles`,
          actions: [
            'Etudes anterieures au Canada: 5 points (2+ ans)',
            'Travail anterieur au Canada: 5 ou 10 points (1+ an)',
            'Offre d\'emploi: 5 points',
            'Famille au Canada: epoux/conjoint (5 pts), parent/frere/soeur (5 pts)'
          ]
        });
      }
      
      // Afficher les recommandations par ordre de priorité
      const prioriteOrdre = { 'HAUTE': 1, 'MOYENNE': 2, 'BASSE': 3, 'INFO': 4 };
      recommandations.sort((a, b) => prioriteOrdre[a.priorite] - prioriteOrdre[b.priorite]);
      
      for (const reco of recommandations) {
        // Vérifier si on a besoin d'une nouvelle page
        if (yPosition > 240) {
          doc.addPage();
          await ajouterFiligraneNouvellePage();
          yPosition = 20;
        }
        
        // Badge de priorité
        const couleurPriorite = {
          'HAUTE': [220, 38, 38],
          'MOYENNE': [255, 165, 0],
          'BASSE': [59, 130, 246],
          'INFO': [107, 114, 128]
        };
        
        doc.setFillColor(...(couleurPriorite[reco.priorite] || [107, 114, 128]));
        doc.roundedRect(20, yPosition - 3, 22, 5, 1, 1, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(7);
        doc.setFont(undefined, 'bold');
        doc.text(reco.priorite, 22, yPosition);
        
        // Titre du domaine
        doc.setTextColor(...textColor);
        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text(reco.domaine, 45, yPosition);
        
        // Score actuel et gain potentiel
        doc.setFontSize(8);
        doc.setFont(undefined, 'normal');
        doc.text(`${reco.score} | Gain: ${reco.gain}`, 45, yPosition + 5);
        
        yPosition += 10;
        
        // Actions recommandées
        doc.setFontSize(8);
        doc.setFont(undefined, 'normal');
        for (const action of reco.actions) {
          const lignes = doc.splitTextToSize(`  • ${action}`, 170);
          for (const ligne of lignes) {
            if (yPosition > 280) {
              doc.addPage();
              await ajouterFiligraneNouvellePage();
              yPosition = 20;
            }
            doc.text(ligne, 25, yPosition);
            yPosition += 4;
          }
        }
        
        yPosition += 6;
      }
      
      // Message d'encouragement
      yPosition += 5;
      doc.setFillColor(220, 252, 231); // Fond vert clair
      doc.rect(15, yPosition - 5, 180, 15, 'F');
      doc.setTextColor(22, 163, 74);
      doc.setFontSize(9);
      doc.setFont(undefined, 'bold');
      doc.text('Avec les bonnes actions, vous pouvez augmenter significativement votre score!', 20, yPosition);
      yPosition += 5;
      doc.setFont(undefined, 'normal');
      doc.setFontSize(8);
      doc.text(`Contactez ${companyName} pour un accompagnement personnalise dans votre demarche d'immigration.`, 20, yPosition + 2);
      
      yPosition += 20;
    }
    
    // Note finale compacte
    doc.setFontSize(8);
    doc.setFont(undefined, 'italic');
    doc.setTextColor(...textColor);
    doc.text('Note: Calculateur pour le Programme des travailleurs qualifies (federal). Points differents du SCG Entree express.', 20, yPosition);
    
    // Sauvegarde du PDF
    const fileName = `admissibilite-entree-express-${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  };

  return (
    <div className="immigration-calculator-container" style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(to bottom right, #fef2f2, #ffffff)', 
      padding: '24px' 
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ 
          backgroundColor: 'white', 
          borderRadius: '8px', 
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', 
          padding: '32px' 
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Calculator style={{ width: '32px', height: '32px', color: '#dc2626' }} />
              <h1 style={{ 
                fontSize: '30px', 
                fontWeight: 'bold', 
                color: '#1f2937',
                margin: 0
              }}>
                Calculateur d'admissibilité - Programme des travailleurs qualifiés (fédéral)
              </h1>
            </div>
            
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {/* Bouton Calculateur Entrée Express */}
              <button
                onClick={() => {
                  // Dispatch custom event to switch module in Dashboard
                  window.dispatchEvent(new CustomEvent('changeModule', { detail: 'entree-express' }));
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  backgroundColor: '#059669',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '12px 20px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  flexShrink: 0
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#047857'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#059669'}
              >
                <Calculator style={{ width: '16px', height: '16px' }} />
                Score SCG Entrée Express
                <ArrowRight style={{ width: '16px', height: '16px' }} />
              </button>
              
              {/* Bouton PDF en en-tête - toujours visible */}
              <button
                onClick={() => generatePDF()}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '12px 20px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  flexShrink: 0
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#b91c1c'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#dc2626'}
              >
                <Download style={{ width: '16px', height: '16px' }} />
                Télécharger PDF
              </button>
            </div>
          </div>

          {/* Sélection du client / lead */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
              Calculer pour un client ou un lead (optionnel)
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                value={selectedPerson ? selectedPerson.label : personQuery}
                onChange={(e) => { setSelectedPerson(null); setPersonQuery(e.target.value); }}
                placeholder="Rechercher par nom, email ou téléphone..."
                style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '6px', padding: '10px 12px' }}
              />
              {(personQuery && !selectedPerson) && (
                <div style={{ position: 'absolute', zIndex: 10, top: '44px', left: 0, right: 0, background: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', maxHeight: '260px', overflowY: 'auto', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}>
                  {isSearching && (
                    <div style={{ padding: '10px 12px', fontSize: '13px', color: '#6b7280' }}>Recherche...</div>
                  )}
                  {!isSearching && personResults.length === 0 && (
                    <div style={{ padding: '10px 12px', fontSize: '13px', color: '#6b7280' }}>Aucun résultat</div>
                  )}
                  {personResults.map((r) => (
                    <div
                      key={`${r.type}-${r.id}`}
                      onClick={() => { setSelectedPerson(r); setPersonQuery(''); }}
                      style={{ padding: '10px 12px', fontSize: '14px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'white'}
                    >
                      <span>{r.label}</span>
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>{r.type}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {selectedPerson && (
              <div style={{ marginTop: '8px', display: 'inline-flex', alignItems: 'center', gap: '8px', background: '#f3f4f6', borderRadius: '999px', padding: '6px 10px', fontSize: '13px', color: '#374151' }}>
                <span style={{ fontWeight: 600 }}>{selectedPerson.type === 'client' ? 'Client' : 'Lead'}:</span>
                <span>{selectedPerson.label}</span>
                <button onClick={() => setSelectedPerson(null)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#6b7280' }}>×</button>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
            {/* Compétences linguistiques - Langue 1 */}
            <section style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: '24px' }}>
              <h2 style={{ 
                fontSize: '20px', 
                fontWeight: '600', 
                color: '#374151', 
                marginBottom: '16px' 
              }}>
                1. Compétences linguistiques - Première langue officielle (max 24 points)
              </h2>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr', 
                gap: '16px' 
              }}>
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '14px', 
                    fontWeight: '500', 
                    color: '#374151', 
                    marginBottom: '8px' 
                  }}>
                    Expression orale
                  </label>
                  <select
                    value={langue1Oral}
                    onChange={(e) => setLangue1Oral(e.target.value)}
                    style={{ 
                      width: '100%', 
                      border: '1px solid #d1d5db', 
                      borderRadius: '6px', 
                      padding: '8px' 
                    }}
                  >
                    <option value="">Sélectionner...</option>
                    <option value="9+">Niveau 9+ NCLC (6 points)</option>
                    <option value="8">Niveau 8 NCLC (5 points)</option>
                    <option value="7">Niveau 7 NCLC (4 points)</option>
                    <option value="moins7">Moins que niveau 7 (0 points)</option>
                  </select>
                </div>
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '14px', 
                    fontWeight: '500', 
                    color: '#374151', 
                    marginBottom: '8px' 
                  }}>
                    Compréhension de l'oral
                  </label>
                  <select
                    value={langue1Ecoute}
                    onChange={(e) => setLangue1Ecoute(e.target.value)}
                    style={{ 
                      width: '100%', 
                      border: '1px solid #d1d5db', 
                      borderRadius: '6px', 
                      padding: '8px' 
                    }}
                  >
                    <option value="">Sélectionner...</option>
                    <option value="9+">Niveau 9+ NCLC (6 points)</option>
                    <option value="8">Niveau 8 NCLC (5 points)</option>
                    <option value="7">Niveau 7 NCLC (4 points)</option>
                    <option value="moins7">Moins que niveau 7 (0 points)</option>
                  </select>
                </div>
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '14px', 
                    fontWeight: '500', 
                    color: '#374151', 
                    marginBottom: '8px' 
                  }}>
                    Compréhension Écrite
                  </label>
                  <select
                    value={langue1Lecture}
                    onChange={(e) => setLangue1Lecture(e.target.value)}
                    style={{ 
                      width: '100%', 
                      border: '1px solid #d1d5db', 
                      borderRadius: '6px', 
                      padding: '8px' 
                    }}
                  >
                    <option value="">Sélectionner...</option>
                    <option value="9+">Niveau 9+ NCLC (6 points)</option>
                    <option value="8">Niveau 8 NCLC (5 points)</option>
                    <option value="7">Niveau 7 NCLC (4 points)</option>
                    <option value="moins7">Moins que niveau 7 (0 points)</option>
                  </select>
                </div>
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '14px', 
                    fontWeight: '500', 
                    color: '#374151', 
                    marginBottom: '8px' 
                  }}>
                    Expression Écrite
                  </label>
                  <select
                    value={langue1Ecriture}
                    onChange={(e) => setLangue1Ecriture(e.target.value)}
                    style={{ 
                      width: '100%', 
                      border: '1px solid #d1d5db', 
                      borderRadius: '6px', 
                      padding: '8px' 
                    }}
                  >
                    <option value="">Sélectionner...</option>
                    <option value="9+">Niveau 9+ NCLC (6 points)</option>
                    <option value="8">Niveau 8 NCLC (5 points)</option>
                    <option value="7">Niveau 7 NCLC (4 points)</option>
                    <option value="moins7">Moins que niveau 7 (0 points)</option>
                  </select>
                </div>
              </div>
            </section>

            {/* Langue 2 */}
            <section style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: '24px' }}>
              <h2 style={{ 
                fontSize: '20px', 
                fontWeight: '600', 
                color: '#374151', 
                marginBottom: '16px' 
              }}>
                2. Seconde langue officielle (max 4 points)
              </h2>
              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: '14px', 
                  fontWeight: '500', 
                  color: '#374151', 
                  marginBottom: '8px' 
                }}>
                  Avez-vous au moins le niveau 5 NCLC pour les 4 compétences?
                </label>
                <select
                  value={langue2}
                  onChange={(e) => setLangue2(e.target.value)}
                  style={{ 
                    width: '100%', 
                    border: '1px solid #d1d5db', 
                    borderRadius: '6px', 
                    padding: '8px' 
                  }}
                >
                  <option value="non">Non (0 points)</option>
                  <option value="oui">Oui (4 points)</option>
                </select>
              </div>
            </section>

            {/* Études */}
            <section style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: '24px' }}>
              <h2 style={{ 
                fontSize: '20px', 
                fontWeight: '600', 
                color: '#374151', 
                marginBottom: '16px' 
              }}>
                3. Études (max 25 points)
              </h2>
              <select
                value={etudes}
                onChange={(e) => setEtudes(e.target.value)}
                style={{ 
                  width: '100%', 
                  border: '1px solid #d1d5db', 
                  borderRadius: '6px', 
                  padding: '8px' 
                }}
              >
                <option value="0">Sélectionner...</option>
                <option value="5">Diplôme d'études secondaires (5 points)</option>
                <option value="15">Programme d'un an postsecondaire (15 points)</option>
                <option value="19">Programme de deux ans postsecondaire (19 points)</option>
                <option value="21">Baccalauréat de 3 ans ou plus (21 points)</option>
                <option value="22">Deux diplômes ou plus dont un de 3 ans+ (22 points)</option>
                <option value="23">Diplôme de niveau maîtrise d'un an ou plus (23 points)</option>
                <option value="25">Doctorat (25 points)</option>
              </select>
            </section>

            {/* Expérience */}
            <section style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: '24px' }}>
              <h2 style={{ 
                fontSize: '20px', 
                fontWeight: '600', 
                color: '#374151', 
                marginBottom: '16px' 
              }}>
                4. Expérience de travail (max 15 points)
              </h2>
              <select
                value={experience}
                onChange={(e) => setExperience(e.target.value)}
                style={{ 
                  width: '100%', 
                  border: '1px solid #d1d5db', 
                  borderRadius: '6px', 
                  padding: '8px' 
                }}
              >
                <option value="">Sélectionner...</option>
                <option value="1">1 an (9 points)</option>
                <option value="2-3">2 ou 3 ans (11 points)</option>
                <option value="4-5">4 ou 5 ans (13 points)</option>
                <option value="6+">6 ans ou plus (15 points)</option>
              </select>
            </section>

            {/* Âge */}
            <section style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: '24px' }}>
              <h2 style={{ 
                fontSize: '20px', 
                fontWeight: '600', 
                color: '#374151', 
                marginBottom: '16px' 
              }}>
                5. Âge (max 12 points)
              </h2>
              <select
                value={age}
                onChange={(e) => setAge(e.target.value)}
                style={{ 
                  width: '100%', 
                  border: '1px solid #d1d5db', 
                  borderRadius: '6px', 
                  padding: '8px' 
                }}
              >
                <option value="">Sélectionner...</option>
                <option value="moins18">Moins de 18 ans (0 points)</option>
                <option value="18-35">18-35 ans (12 points)</option>
                <option value="36">36 ans (11 points)</option>
                <option value="37">37 ans (10 points)</option>
                <option value="38">38 ans (9 points)</option>
                <option value="39">39 ans (8 points)</option>
                <option value="40">40 ans (7 points)</option>
                <option value="41">41 ans (6 points)</option>
                <option value="42">42 ans (5 points)</option>
                <option value="43">43 ans (4 points)</option>
                <option value="44">44 ans (3 points)</option>
                <option value="45">45 ans (2 points)</option>
                <option value="46">46 ans (1 point)</option>
                <option value="47+">47 ans et plus (0 points)</option>
              </select>
            </section>

            {/* Emploi réservé */}
            <section style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: '24px' }}>
              <h2 style={{ 
                fontSize: '20px', 
                fontWeight: '600', 
                color: '#374151', 
                marginBottom: '16px' 
              }}>
                6. Emploi réservé au Canada (max 10 points)
              </h2>
              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: '14px', 
                  fontWeight: '500', 
                  color: '#374151', 
                  marginBottom: '8px' 
                }}>
                  Avez-vous une offre d'emploi valide?
                </label>
                <select
                  value={emploiReserve}
                  onChange={(e) => setEmploiReserve(e.target.value)}
                  style={{ 
                    width: '100%', 
                    border: '1px solid #d1d5db', 
                    borderRadius: '6px', 
                    padding: '8px' 
                  }}
                >
                  <option value="non">Non (0 points)</option>
                  <option value="oui">Oui (10 points)</option>
                </select>
              </div>
            </section>

            {/* Faculté d'adaptation */}
            <section style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: '24px' }}>
              <h2 style={{ 
                fontSize: '20px', 
                fontWeight: '600', 
                color: '#374151', 
                marginBottom: '16px' 
              }}>
                7. Faculté d'adaptation (max 10 points)
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <input
                    type="checkbox"
                    onChange={(e) => handleAdaptationChange('5', e.target.checked)}
                    style={{ marginTop: '4px' }}
                  />
                  <span style={{ fontSize: '14px' }}>Compétences linguistiques du conjoint niveau 4+ NCLC (5 points)</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <input
                    type="checkbox"
                    onChange={(e) => handleAdaptationChange('5', e.target.checked)}
                    style={{ marginTop: '4px' }}
                  />
                  <span style={{ fontSize: '14px' }}>Vos études antérieures au Canada (2+ ans) (5 points)</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <input
                    type="checkbox"
                    onChange={(e) => handleAdaptationChange('5', e.target.checked)}
                    style={{ marginTop: '4px' }}
                  />
                  <span style={{ fontSize: '14px' }}>Études antérieures du conjoint au Canada (2+ ans) (5 points)</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <input
                    type="checkbox"
                    onChange={(e) => handleAdaptationChange('10', e.target.checked)}
                    style={{ marginTop: '4px' }}
                  />
                  <span style={{ fontSize: '14px' }}>Votre travail antérieur au Canada (1+ an) (10 points)</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <input
                    type="checkbox"
                    onChange={(e) => handleAdaptationChange('5', e.target.checked)}
                    style={{ marginTop: '4px' }}
                  />
                  <span style={{ fontSize: '14px' }}>Travail antérieur du conjoint au Canada (1+ an) (5 points)</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <input
                    type="checkbox"
                    onChange={(e) => handleAdaptationChange('5', e.target.checked)}
                    style={{ marginTop: '4px' }}
                  />
                  <span style={{ fontSize: '14px' }}>Emploi réservé au Canada (5 points)</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <input
                    type="checkbox"
                    onChange={(e) => handleAdaptationChange('5', e.target.checked)}
                    style={{ marginTop: '4px' }}
                  />
                  <span style={{ fontSize: '14px' }}>Membre de la famille au Canada (5 points)</span>
                </label>
                <p style={{ 
                  fontSize: '12px', 
                  color: '#6b7280', 
                  marginTop: '8px' 
                }}>
                  Note: Maximum combiné de 10 points pour cette section
                </p>
              </div>
            </section>

            {/* Résultat */}
            <div style={{
              borderRadius: '8px',
              padding: '24px',
              backgroundColor: isEligible ? '#f0fdf4' : '#fef2f2',
              border: `2px solid ${isEligible ? '#22c55e' : '#ef4444'}`
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                {isEligible ? (
                  <CheckCircle style={{ width: '40px', height: '40px', color: '#16a34a' }} />
                ) : (
                  <XCircle style={{ width: '40px', height: '40px', color: '#dc2626' }} />
                )}
                <div>
                  <h3 style={{ 
                    fontSize: '24px', 
                    fontWeight: 'bold', 
                    color: '#1f2937',
                    margin: 0
                  }}>
                    Votre score: {totalPoints} / 100 points
                  </h3>
                  <p style={{ 
                    fontSize: '18px', 
                    color: isEligible ? '#15803d' : '#dc2626',
                    margin: '4px 0 0 0'
                  }}>
                    {isEligible 
                      ? '✓ Vous êtes admissible! (67+ points requis)' 
                      : `✗ Non admissible. Il vous manque ${67 - totalPoints} point(s).`}
                  </p>
                </div>
              </div>
              <p style={{ fontSize: '14px', color: '#4b5563', margin: '0 0 16px 0' }}>
                {isEligible 
                  ? 'Vous pouvez soumettre un profil au bassin d\'Entrée express. Note: Ces points sont différents du système de classement global (SCG) utilisé dans le bassin d\'Entrée express.' 
                  : 'Vous devez obtenir au moins 67 points pour être admissible au Programme des travailleurs qualifiés (fédéral).'}
              </p>
              
              {/* Bouton de téléchargement PDF */}
              <button
                onClick={() => generatePDF()}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '12px 20px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#b91c1c'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#dc2626'}
              >
                <Download style={{ width: '16px', height: '16px' }} />
                Télécharger le rapport PDF
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
